import { InvestorDataSettings } from "@shared/models/InvestorDataSettings";
import { DetailedCounty, User } from "@shared/models/User";
import { ZipCode } from "@shared/models/ZipCode";
import { HubspotPropertiesHistory } from "@shared/models/HubspotPropertiesHistory";
import { PaymentMethodType } from "@shared/interfaces";

export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELLED = "cancelled",
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  NON_RENEWING = "non_renewing",
  PAST_DUE = "past_due",
  TRIALING = "trialing",
  UNPAID = "unpaid",
}

export enum SubscriptionType {
  STANDARD = "standard",
  NICHE = "niche",
}

export enum AccountFilterStatus {
  active = "active",
  paused = "paused",
  past_due = "past_due",
  non_renewing = "non_renewing",
  cancelled = "cancelled",
  incomplete_expired = "incomplete_expired",
  inactive = "inactive",
  all = "all",
}

export enum ChargebeeRecordType {
  PayPerLead = "ppl",
  NicheInvestor = "stacked_niche_investor",
  Investor = "Investor",
  Realtor = "Real Estate",
  flikDigital = "Flik Digital",
  flikPPC = "Flik PPC",
  NicheRealtor = "Stacked Niche Realtor",
}

export const ChargebeeRecordTypeMapper: Record<ChargebeeRecordType, string> = {
  [ChargebeeRecordType.PayPerLead]: "PPL",
  [ChargebeeRecordType.NicheInvestor]: "Stacked Niche Investor",
  [ChargebeeRecordType.Investor]: "Investor",
  [ChargebeeRecordType.Realtor]: "Realtor",
  [ChargebeeRecordType.flikDigital]: "Flik Digital",
  [ChargebeeRecordType.flikPPC]: "Flik PPC",
  [ChargebeeRecordType.NicheRealtor]: "Stacked Niche Realtor",
};

export interface Subscription {
  id: number;
  status: SubscriptionStatus;
  subscription_properties: {
    id: number;
    subscription_id: number;
    subscription_name: string;
    subscription_description: string | null;
    deal_name: string | null;
    chargebee_record_type?: ChargebeeRecordType | null;
  } | null;
  stripe_id: string;
  chargebee_subscription_id: string;
  counties: DetailedCounty[];
  zip_codes: ZipCode[];
  account?: Partial<Account>;
  type: SubscriptionType;
  cancelled_at?: Date | string;
  next_billing_at?: Date | string;
  activated_at?: Date | string;
}

export interface Account {
  id: number;
  name: string;
  credits: number;
  is_active: boolean;
  claritas_id: string | null;
  claritas_nickname: string | null;
  chargebee_customer_id: string;
  created_at: Date;
  updated_at: Date | null;
  created_by: string;
  updated_by: string | null;
  investor_data_settings: InvestorDataSettings | null;
  subscriptions?: Subscription[];
  hubspot_company_id?: number | null;
  state_id: number | null;
  county_id: number | null;
  buy_box?: string | null;
  users?: User[];
  zip_codes?: ZipCode[];
  hubspot_properties_history?: HubspotPropertiesHistory[];
  primary_user?: User;
  user_id?: number;
  reisift?: {
    id: number;
    account_id: number;
    rei_sift_account_uuid: string;
  };
}

export interface AccountScmManager {
  firstName: string;
  lastName: string;
  email: string;
  avatar: string;
}

export interface PaymentMethodExistence {
  exists: boolean | null;
  cardExists: boolean | null;
}

export interface AccountPropertyList {
  id: number;
  name: string;
  account_id: number;
  counties_total: number;
  properties_total: number;
  created_at: Date | null;
}

export interface AccountNote {
  id: number;
  text: string;
  user: User;
  account_id: number;
  created_at: Date;
}

export interface GetAccountEntity {
  type: string;
  entity: string;
}

export interface SaveAccountAttempts {
  call: number;
  mail: number;
  message: number;
  type: string;
  entity: string;
  hideLoader?: boolean;
}

export interface UpdatePrimaryUserPayload {
  user_id: number;
  account_id: number;
}

export interface AccountAttempts {
  id: number;
  call: number;
  mail: number;
  message: number;
  property_id: number;
  account_id: number;
  created_at: Date;
}

export interface AccountPaymentMethodsParams {
  type?: PaymentMethodType;
}

export interface AdjustAccountBalanceRequest {
  account_id: number;
  amount_in_cents: number;
  type: string;
  reason: string;
}

export interface Discount {
  percent_off?: number;
  amount_off?: number;
}
